import React, { useContext, useState, memo, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import gallery from "../Svgs/gallery.svg";
import { UserContext, useUpdateUser, useUser } from "../../Context/UserContext";
import Heart from "../Svgs/Heart";
import Trash from "../Svgs/Trash";

const perfectUrl = (url) => {
  return url.startsWith('http') ? url : "https://" + url;
};

const AkiyaCard = memo(({ item, isFavorite = false, onDelete }) => {
  const {
    images,
    prefectureName,
    cityName,
    total_area,
    land_area,
    built,
    price,
    post_id,
  } = item;

  const navigate = useNavigate();
  const user = useUser();
  const UpdateUser = useUpdateUser();
  const { showModal, setShowModal } = useContext(UserContext);
  
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isValidImage, setIsValidImage] = useState(true);

  // Simplified and cleaner data formatting
  const formattedData = useMemo(() => {
    // Clean area formatting
    const formatArea = (area) => {
      if (!area) return null;
      const match = area.match(/[\d,.]+/);
      if (!match) return null;
      const number = parseFloat(match[0].replace(/,/g, ''));
      return isNaN(number) ? null : `${number}m²`;
    };

    // Simplified price formatting
    const formatPrice = () => {
      if (!price) return "Price on inquiry";
      const numericPrice = parseFloat(price);
      if (isNaN(numericPrice)) return "Price on inquiry";
      const convertedPrice = numericPrice * 10000;
      return `¥${convertedPrice.toLocaleString()}`;
    };

    // Clean address - prioritize city
    const getDisplayLocation = () => {
      if (cityName && prefectureName) {
        return `${cityName}, ${prefectureName}`;
      }
      return prefectureName || cityName || "Location TBD";
    };

    return {
      landArea: formatArea(land_area),
      houseArea: formatArea(total_area),
      price: formatPrice(),
      location: getDisplayLocation(),
      built: built ? built.replace(/[^\d]/g, '') : null
    };
  }, [land_area, total_area, price, cityName, prefectureName, built]);

  const isFavoritePresent = useMemo(() => 
    user && user.favorite && user.favorite.includes(post_id), 
    [user, post_id]
  );

  const imageSrc = useMemo(() => {
    if (images && images[0] && isValidImage) {
      return perfectUrl(images[0]);
    }
    return gallery;
  }, [images, isValidImage]);

  // Event handlers
  const handleToggleFavorite = useCallback((event) => {
    event.stopPropagation();
    if (!user) {
      setShowModal(true);
    } else {
      const favoriteData = isFavoritePresent
        ? user.favorite.filter((f) => f !== post_id)
        : [...user.favorite, post_id];
      UpdateUser({ ...user, favorite: favoriteData });
    }
  }, [user, isFavoritePresent, post_id, setShowModal, UpdateUser]);

  const handleCardClick = useCallback(() => {
    navigate(`/individual_kominka/${post_id}`);
  }, [navigate, post_id]);

  const handleDelete = useCallback((event) => {
    event.stopPropagation();
    onDelete(post_id);
  }, [onDelete, post_id]);

  const handleImageLoad = useCallback(() => {
    setIsValidImage(true);
    setImageLoaded(true);
  }, []);

  const handleImageError = useCallback(() => {
    setIsValidImage(false);
    setImageLoaded(true);
  }, []);

  return (
    <div
      className="group cursor-pointer h-full"
      onClick={handleCardClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Simplified card with cleaner styling */}
      <article className="bg-white rounded-xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 ease-out transform sm:hover:scale-[1.01] sm:hover:-translate-y-1 h-full flex flex-col">
        
        {/* Cleaner Image Section */}
        <div className="aspect-[4/3] w-full relative overflow-hidden">
          <img
            loading="lazy"
            className={`w-full h-full object-cover transition-transform duration-500 ${
              isHovered ? 'sm:scale-105' : 'scale-100'
            }`}
            src={imageSrc}
            alt="Traditional Japanese House"
            onLoad={handleImageLoad}
            onError={handleImageError}
          />
          
          {/* Simplified loading state */}
          {!imageLoaded && (
            <div className="absolute inset-0 bg-gray-200 animate-pulse"></div>
          )}
          
          {/* Minimal favorite button */}
          {!isFavorite && (
            <button
              onClick={handleToggleFavorite}
              className={`absolute top-3 right-3 w-9 h-9 rounded-full backdrop-blur-md transition-all duration-200 flex items-center justify-center ${
                isFavoritePresent
                  ? 'bg-white/95 text-red-500 shadow-md'
                  : 'bg-black/30 text-white hover:bg-white/95 hover:text-red-500 hover:shadow-md'
              }`}
            >
              <Heart className="w-5 h-5" />
            </button>
          )}

          {/* Clean price overlay */}
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
            <div className="text-white text-xl font-bold">
              {formattedData.price}
            </div>
          </div>
        </div>

        {/* Simplified content section */}
        <div className="p-4 flex-1 flex flex-col space-y-3">
          {/* Location */}
          <div>
            <h3 className="font-semibold text-gray-900 text-lg leading-tight line-clamp-2">
              {formattedData.location}
            </h3>
          </div>
          
          {/* Key details in clean layout */}
          <div className="flex items-center justify-between text-sm text-gray-600 flex-wrap gap-2">
            {formattedData.landArea && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                {formattedData.landArea}
              </span>
            )}
            
            {formattedData.houseArea && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a1 1 0 00-.293-.707L12 0.586a1 1 0 00-1.414 0L1.293 8.293A1 1 0 001 9v8a2 2 0 002 2z" />
                </svg>
                {formattedData.houseArea}
              </span>
            )}
            
            {formattedData.built && (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7V3a1 1 0 011-1h6a1 1 0 011 1v4m-6 0h6m-6 0V3m6 4v10a2 2 0 01-2 2H10a2 2 0 01-2-2V7" />
                </svg>
                {formattedData.built}
              </span>
            )}
          </div>

          {/* Clean CTA button */}
          <div className="mt-auto pt-2">
            <div className={`w-full text-center py-2.5 rounded-lg font-medium text-sm transition-all duration-200 ${
              isHovered 
                ? 'bg-[#e09321] text-white shadow-md' 
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}>
              View Details
            </div>
          </div>

          {/* Delete button for favorites - minimal */}
          {isFavorite && (
            <button
              className="absolute top-3 right-3 w-8 h-8 bg-red-50 hover:bg-red-100 rounded-lg flex items-center justify-center transition-colors duration-200"
              onClick={handleDelete}
            >
              <Trash className="w-4 h-4 text-red-600" />
            </button>
          )}
        </div>
      </article>
    </div>
  );
});

AkiyaCard.displayName = 'CleanKominkaCard';

export default AkiyaCard;